import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useRouter, useSearch } from "@tanstack/react-router";
import { useMultiStepForm } from "web-design-system";
import {
  investorProfileQueryOptions,
  useCreateOrLinkHolderProfile,
  useInvalidateClient,
  useUpdateInvestorProfileDetails,
} from "creso-core/hooks";
import { AddSecondHolderFormData } from "creso-core/types";
import { buildHolderProfilePayload, findSecondHolder } from "creso-core/utils";
import { handleError } from "@/utils/error";

// Called once both holders have approved: refetch the client (and the route
// loader that feeds the status bar) so the pending-approval banner goes away.
export function useRefetchClientStatus(clientId: number) {
  const router = useRouter();
  const invalidateClient = useInvalidateClient(clientId);
  return async () => {
    await invalidateClient();
    await router.invalidate();
  };
}

export function useSecondHolderClientId() {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false });
  return Number(search.client_id ?? params.id ?? 0);
}

export function useSaveSecondHolderProfile() {
  const clientId = useSecondHolderClientId();
  const { formData, updateFormData } =
    useMultiStepForm<AddSecondHolderFormData>();
  const { data: profiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: clientId > 0 }
    )
  );
  const secondHolder = findSecondHolder(profiles);
  const { mutateAsync: createProfile, isPending: isCreating } =
    useCreateOrLinkHolderProfile();
  const { mutateAsync: updateProfile, isPending: isUpdating } =
    useUpdateInvestorProfileDetails();

  const existingProfileGuid =
    formData.investor_profile_guid ?? secondHolder?.investor_profile_guid;
  const savedGuidRef = useRef(existingProfileGuid);

  const saveHolderProfile = async (
    stepData: Partial<AddSecondHolderFormData>
  ) => {
    if (!clientId) {
      throw new Error("Missing client");
    }

    const savedGuid =
      formData.investor_profile_guid ??
      savedGuidRef.current ??
      secondHolder?.investor_profile_guid;
    // Set when an existing client is picked: their profile gets linked.
    const linkedGuid = stepData.investor_profile_guid;

    if (savedGuid && (!linkedGuid || linkedGuid === savedGuid)) {
      await updateProfile(
        buildHolderProfilePayload(clientId, {
          ...formData,
          ...stepData,
          investor_profile_guid: savedGuid,
        })
      );
      savedGuidRef.current = savedGuid;
      return savedGuid;
    }

    const payload = buildHolderProfilePayload(clientId, {
      ...formData,
      ...stepData,
      investor_profile_guid: linkedGuid,
    });
    const profile = await createProfile(payload);
    savedGuidRef.current = profile.investor_profile_guid;
    return profile.investor_profile_guid;
  };

  const saveAndContinue = async (
    stepData: Partial<AddSecondHolderFormData>
  ) => {
    try {
      const guid = await saveHolderProfile(stepData);
      updateFormData({
        ...stepData,
        ...(guid ? { investor_profile_guid: guid } : {}),
      });
      return true;
    } catch (error) {
      handleError(error, "Failed to save second holder details");
      return false;
    }
  };

  return {
    saveAndContinue,
    saveHolderProfile,
    isPending: isCreating || isUpdating,
    secondHolder,
    clientId,
  };
}
